import type { StandardListParams } from '../types/list';
import { ApiService } from './ApiService';

/**
 * AI Agent Tool definition - shared across automation and ai-agents module
 */
export interface AiAgentTool {
	id: number;
	_id?: number;
	name: string;
	type: string;
	description?: string;
	metadata?: Record<string, unknown>;
	createdDate?: string;
	modifiedDate?: string;
}

export interface AiAgentToolsResponse {
	success: boolean;
	data: Array<AiAgentTool>;
	pagination?: {
		total: number;
		totalPages: number;
		page: number;
		limit: number;
	};
	error?: string;
}

/**
 * Expected API response structure from /ai-agents/tools endpoint
 */
interface ToolsApiResponse {
	data?: Array<AiAgentTool>;
	pageInfo?: {
		total: number;
		pages: number;
		page: number;
		limit: number;
	};
	error?: string;
}

/**
 * Query service for AI Agent Tools
 * Provides read-only access to tools for selection in UI components
 */
class AiAgentToolsQueryServiceClass {
	private baseUrl = '/ai-agents/tools';

	/**
	 * Get all available tools for selection
	 * Used by AgentToolSelector component in both automation and ai-agents contexts
	 */
	async getAvailableTools(
		search?: string,
		limit: number = 100,
	): Promise<AiAgentToolsResponse> {
		return this.getTools({
			page: 1,
			pageSize: limit,
			search,
			fields: ['name', 'type', 'description', 'metadata'],
		});
	}

	/**
	 * Get tools with pagination and filtering
	 */
	async getTools(
		params: StandardListParams = {},
	): Promise<AiAgentToolsResponse> {
		try {
			const queryParams = new URLSearchParams();
			queryParams.append('page', (params.page ?? 1).toString());
			queryParams.append('limit', (params.pageSize ?? 25).toString());

			if (params.search != null) {
				// API uses 'toolSearch' parameter for full-text search
				queryParams.append('toolSearch', params.search);
			}

			if (params.sort != null) {
				queryParams.append('sortFields', JSON.stringify(params.sort));
			}

			if (params.fields != null && params.fields.length > 0) {
				queryParams.append('fields', params.fields.join(','));
			}

			if (params.cacheKey != null) {
				queryParams.append('cacheKey', params.cacheKey);
			}

			if (params.cacheTTL != null) {
				queryParams.append('cacheTTL', params.cacheTTL.toString());
			}

			const filters = params.filters as Record<string, unknown> | undefined;

			if (filters != null) {
				Object.keys(filters).forEach((filterName) => {
					const filterValue = filters[filterName];

					if (
						filterValue !== undefined &&
						filterValue !== null &&
						filterValue !== ''
					) {
						if (Array.isArray(filterValue)) {
							queryParams.append(filterName, filterValue.join(','));
						} else if (typeof filterValue === 'object') {
							queryParams.append(filterName, JSON.stringify(filterValue));
						} else {
							queryParams.append(filterName, String(filterValue));
						}
					}
				});
			}

			const url = `${this.baseUrl}?${queryParams.toString()}`;
			const response = await ApiService.apiGet<ToolsApiResponse>(url);

			if (response.data != null) {
				return {
					success: true,
					data: response.data ?? [],
					pagination:
						response.pageInfo != null
							? {
									total: response.pageInfo.total,
									totalPages: response.pageInfo.pages,
									page: response.pageInfo.page,
									limit: response.pageInfo.limit,
								}
							: undefined,
				};
			}

			return {
				success: false,
				data: [],
				error: response.error ?? 'Failed to fetch tools',
			};
		} catch (error) {
			return {
				success: false,
				data: [],
				error:
					error instanceof Error
						? error.message
						: 'An unexpected error occurred',
			};
		}
	}
}

export const AiAgentToolsQueryService = new AiAgentToolsQueryServiceClass();
